import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Upload } from "antd";
import type { UploadProps } from "antd";
import { t } from "i18next";
import type { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { compressImageToLimit, isLossyCompressibleImage } from "@utils/imageUpload/preprocess";
import type { ActiveUploadTask, ClipUploadFile, ClipUploadProps, InternalUploadingItem } from "./types";
import { createPreload$, generateUid, isAbortError, matchesAccept, resolveCompressOptions } from "./utils";
import { isHttpErrorToastShown } from "@apis/http/httpErrorToast";

export interface UseClipUploadOptions {
  files?: ClipUploadFile[];
  defaultFiles?: ClipUploadFile[];
  onFilesChange?: (files: ClipUploadFile[]) => void;
  uploadFile?: ClipUploadProps["uploadFile"];
  beforeUpload?: ClipUploadProps["beforeUpload"];
  maxCount?: number;
  accept?: string[];
  onUnsupportedFileType?: ClipUploadProps["onUnsupportedFileType"];
  compressImage?: ClipUploadProps["compressImage"];
  disabled?: boolean;
}

export interface UseClipUploadResult {
  currentFiles: ClipUploadFile[];
  uploadingItems: InternalUploadingItem[];
  previewUrls: Record<string, string>;
  showPlaceholder: boolean;
  acceptString: string;
  handleRemoveDone: (uid: string) => void;
  handleRemoveUploading: (uid: string) => void;
  handleBeforeUpload: NonNullable<UploadProps["beforeUpload"]>;
  handleCustomRequest: NonNullable<UploadProps["customRequest"]>;
  handleDrop: NonNullable<UploadProps["onDrop"]>;
}

export const useClipUpload = ({
  files,
  defaultFiles,
  onFilesChange,
  uploadFile,
  beforeUpload,
  maxCount = 1,
  accept,
  onUnsupportedFileType,
  compressImage = false,
  disabled = false,
}: UseClipUploadOptions): UseClipUploadResult => {
  const isControlled = files !== undefined;
  const [internalFiles, setInternalFiles] = useState<ClipUploadFile[]>(() => defaultFiles ?? []);
  const currentFiles = isControlled ? files : internalFiles;
  const filesRef = useRef<ClipUploadFile[]>(currentFiles);
  filesRef.current = currentFiles;

  const [uploadingItems, setUploadingItems] = useState<InternalUploadingItem[]>([]);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const previewUrlsRef = useRef(new Map<string, string>());
  const preloadSubsRef = useRef(new Map<string, Subscription>());
  const cancelledRef = useRef(new Set<string>());
  const activeUploadsRef = useRef(new Map<string, ActiveUploadTask>());

  const releasePreview = useCallback((uid: string) => {
    preloadSubsRef.current.get(uid)?.unsubscribe();
    preloadSubsRef.current.delete(uid);

    const blobUrl = previewUrlsRef.current.get(uid);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      previewUrlsRef.current.delete(uid);
      setPreviewUrls((prev) => {
        if (!(uid in prev)) return prev;
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    }
  }, []);

  const schedulePreviewSwap = useCallback(
    (uid: string, serverUrl: string | undefined, mimeType: string | undefined) => {
      preloadSubsRef.current.get(uid)?.unsubscribe();
      preloadSubsRef.current.delete(uid);

      const sub = createPreload$(serverUrl ?? "", mimeType)
        .pipe(
          finalize(() => {
            preloadSubsRef.current.delete(uid);
          }),
        )
        .subscribe((result) => {
          if (result === "loaded") releasePreview(uid);
        });

      if (!sub.closed) preloadSubsRef.current.set(uid, sub);
    },
    [releasePreview],
  );

  useEffect(() => {
    const activeUploads = activeUploadsRef.current;
    const cancelledUploads = cancelledRef.current;
    const preloadSubs = preloadSubsRef.current;
    const previewUrlsMap = previewUrlsRef.current;
    return () => {
      activeUploads.forEach((task, uid) => {
        cancelledUploads.add(uid);
        task.controller.abort();
        URL.revokeObjectURL(task.previewUrl);
      });
      activeUploads.clear();

      preloadSubs.forEach((sub) => sub.unsubscribe());
      preloadSubs.clear();

      previewUrlsMap.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
      previewUrlsMap.clear();
    };
  }, []);

  useEffect(() => {
    const currentUids = new Set(currentFiles.map((f) => f.uid));
    for (const uid of previewUrlsRef.current.keys()) {
      if (!currentUids.has(uid)) releasePreview(uid);
    }
  }, [currentFiles, releasePreview]);

  const acceptList = useMemo(() => accept ?? [], [accept]);
  const acceptString = acceptList.join(",");
  const compressOptions = useMemo(() => resolveCompressOptions(compressImage), [compressImage]);

  const unsupportedToastAtRef = useRef(0);
  const notifyUnsupportedFileType = useCallback(
    (msg: string) => {
      const now = Date.now();
      if (now - unsupportedToastAtRef.current < 200) return;
      unsupportedToastAtRef.current = now;
      (onUnsupportedFileType ?? message.error)(msg);
    },
    [onUnsupportedFileType],
  );

  const totalCount = currentFiles.length + uploadingItems.length;
  const showPlaceholder = !disabled && totalCount < maxCount;

  const updateFiles = useCallback(
    (next: ClipUploadFile[]) => {
      if (!isControlled) setInternalFiles(next);
      onFilesChange?.(next);
    },
    [isControlled, onFilesChange],
  );

  const handleRemoveDone = useCallback(
    (uid: string) => {
      releasePreview(uid);
      const next = filesRef.current.filter((f) => f.uid !== uid);
      if (next.length !== filesRef.current.length) updateFiles(next);
    },
    [releasePreview, updateFiles],
  );

  const handleRemoveUploading = useCallback((uid: string) => {
    cancelledRef.current.add(uid);
    const task = activeUploadsRef.current.get(uid);
    if (task) {
      task.controller.abort();
      URL.revokeObjectURL(task.previewUrl);
      activeUploadsRef.current.delete(uid);
    }
    setUploadingItems((prev) => {
      const target = prev.find((item) => item.uid === uid);
      if (!task && target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.uid !== uid);
    });
  }, []);

  const handleBeforeUpload: NonNullable<UploadProps["beforeUpload"]> = useCallback(
    async (rawFile, fileList) => {
      // 同批次多文件时利用 fileList 中的位置做名额限制，避免依赖尚未更新的 uploadingItems 状态
      const baseUsed = filesRef.current.length + uploadingItems.length;
      const batchIndex = Math.max(0, fileList.indexOf(rawFile));
      if (baseUsed + batchIndex >= maxCount) return Upload.LIST_IGNORE;

      // 类型校验：与原生 input accept 同时做一次匹配，避免拖拽 / 粘贴绕过 input
      if (acceptList.length > 0 && !matchesAccept(rawFile, acceptList)) {
        notifyUnsupportedFileType(t("Unsupported file type"));
        return Upload.LIST_IGNORE;
      }

      let processed: File = rawFile;
      if (compressOptions && isLossyCompressibleImage(rawFile) && rawFile.size > compressOptions.maxSizeBytes) {
        try {
          processed = await compressImageToLimit(rawFile, compressOptions);
        } catch {
          message.error(t("Image compression failed, please retry"));
          return Upload.LIST_IGNORE;
        }
      }

      if (beforeUpload) {
        const result = await beforeUpload(processed, fileList as File[]);
        if (result === false) return Upload.LIST_IGNORE;
        if (typeof result === "string") {
          message.error(result);
          return Upload.LIST_IGNORE;
        }
      }

      return processed;
    },
    [acceptList, beforeUpload, compressOptions, maxCount, notifyUnsupportedFileType, uploadingItems.length],
  );

  const handleDrop: NonNullable<UploadProps["onDrop"]> = useCallback(
    (event) => {
      if (disabled || acceptList.length === 0) return;
      const dropped = Array.from(event.dataTransfer?.files ?? []);
      if (dropped.some((file) => !matchesAccept(file, acceptList))) {
        notifyUnsupportedFileType(t("Unsupported file type"));
      }
    },
    [acceptList, disabled, notifyUnsupportedFileType],
  );

  const handleCustomRequest: NonNullable<UploadProps["customRequest"]> = useCallback(
    async ({ file, onSuccess, onError, onProgress }) => {
      if (!(file instanceof File)) {
        const err = new Error("Invalid file");
        onError?.(err);
        return;
      }

      const uid = generateUid();
      const controller = new AbortController();
      const previewUrl = URL.createObjectURL(file);
      activeUploadsRef.current.set(uid, { controller, previewUrl });
      setUploadingItems((prev) => [
        ...prev,
        {
          uid,
          name: file.name,
          type: file.type,
          size: file.size,
          percent: 0,
          status: "uploading",
          previewUrl,
        },
      ]);

      const reportProgress = (percent: number) => {
        if (cancelledRef.current.has(uid)) return;
        const clamped = Math.max(0, Math.min(100, percent));
        onProgress?.({ percent: clamped }, file);
        setUploadingItems((prev) => prev.map((item) => (item.uid === uid ? { ...item, percent: clamped } : item)));
      };

      try {
        if (!uploadFile) {
          throw new Error(t("uploadFile prop is required to upload files"));
        }

        const result = await uploadFile(file, { onProgress: reportProgress, signal: controller.signal });
        if (cancelledRef.current.has(uid)) {
          cancelledRef.current.delete(uid);
          activeUploadsRef.current.delete(uid);
          URL.revokeObjectURL(previewUrl);
          return;
        }

        const baseFile: ClipUploadFile = {
          uid,
          name: file.name,
          type: file.type,
          size: file.size,
        };
        const uploaded: ClipUploadFile =
          typeof result === "string" ? { ...baseFile, url: result } : { ...baseFile, ...result };
        const previewUid = uploaded.uid;
        const preloadUrl = uploaded.thumbUrl ?? uploaded.url;
        const preloadType = uploaded.thumbUrl ? "image/*" : (uploaded.type ?? file.type);

        setUploadingItems((prev) => prev.filter((item) => item.uid !== uid));
        activeUploadsRef.current.delete(uid);

        previewUrlsRef.current.set(previewUid, previewUrl);
        setPreviewUrls((prev) => ({ ...prev, [previewUid]: previewUrl }));

        updateFiles([...filesRef.current, uploaded]);
        schedulePreviewSwap(previewUid, preloadUrl, preloadType);
        onSuccess?.({}, file);
      } catch (error) {
        URL.revokeObjectURL(previewUrl);
        activeUploadsRef.current.delete(uid);
        if (cancelledRef.current.has(uid) || controller.signal.aborted || isAbortError(error)) {
          cancelledRef.current.delete(uid);
          return;
        }
        const err = error as Error;
        const errorMessage = err?.message || t("Upload failed");
        setUploadingItems((prev) =>
          prev.map((item) => (item.uid === uid ? { ...item, status: "error", errorMessage } : item)),
        );
        if (!isHttpErrorToastShown(error)) {
          message.error(errorMessage);
        }
        onError?.(err);
      }
    },
    [uploadFile, updateFiles, schedulePreviewSwap],
  );

  return {
    currentFiles,
    uploadingItems,
    previewUrls,
    showPlaceholder,
    acceptString,
    handleRemoveDone,
    handleRemoveUploading,
    handleBeforeUpload,
    handleCustomRequest,
    handleDrop,
  };
};
